import React, { useRef, useEffect } from 'react';
import { FormField } from '@/features/application/types/forms';
import { PathBuilder } from '../components/PathBuilder';
import { Combobox } from '@/features/ui/components/Combobox';
import { CrmField } from '../types';
import { InfoTooltip } from '@/features/ui/components/Tooltip';
import { Button } from '@/features/ui/primitives/button';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { TypeChangeModal } from './validation/TypeChangeModal';
import DuplicateMappingWarningModal from './validation/DuplicateMappingWarningModal';
import MaxLengthValidationModal from './validation/MaxLengthValidationModal';
import LocatorRequiredModal from './validation/LocatorRequiredModal';
import LeadingZeroWarningModal from './validation/LeadingZeroWarningModal';

interface CrmMappingInputProps {
  field: FormField;
  onMappingComplete?: () => void;
  activeStep: 'path' | 'field' | null;
  setActiveStep: (step: 'path' | 'field' | null) => void;
  selectedFieldApiName: string;
  setSelectedFieldApiName: (apiName: string) => void;
  availableFields: CrmField[] | null;
  isLoading: boolean;
  isFieldComplete: boolean;
  isFieldComboboxOpen: boolean;
  setIsFieldComboboxOpen: (isOpen: boolean) => void;
  typeChangeModal: any; // Simplified for brevity
  duplicateModal: any;
  maxLengthModal: any;
  leadingZeroWarningModal: any;
  locatorRequiredModalOpen: boolean;
  setLocatorRequiredModalOpen: (isOpen: boolean) => void;
  handleConfirmStep: (step: 'path' | 'field') => void;
  handlePathChange: (path: any) => void; // Simplified
  handleConfirmMapping: () => void;
  handleTypeChangeModalClose: () => void;
  handleTypeChangeModalConfirm: (newType: any) => void; // Simplified
  handleDuplicateModalClose: () => void;
  handleMaxLengthModalClose: () => void;
  handleLeadingZeroWarningClose: () => void;
}

type ActiveStep = 'path' | 'field'; // Removed 'locator' since it's now inline

const StepHeader: React.FC<{
  title: string;
  step: ActiveStep;
  activeStep: ActiveStep | null;
  setActiveStep: (step: ActiveStep | null) => void;
  isComplete: boolean;
  tooltip: string;
}> = ({ title, step, activeStep, setActiveStep, isComplete, tooltip }) => (
  <button
    className="w-full text-left flex items-center justify-between p-3 bg-gray-50 rounded-t-md border-b"
    onClick={() => setActiveStep(step)}
  >
    <div className="flex items-center">
      {activeStep === step ? (
        <ChevronDown className="h-5 w-5" />
      ) : (
        <ChevronRight className="h-5 w-5" />
      )}
      <h3 className="text-md font-semibold ml-2">{title}</h3>
      <InfoTooltip className="ml-2" text={tooltip} />
    </div>
    {isComplete && <span className="text-xs text-green-600 font-bold">✓ Confirmed</span>}
  </button>
);

export const CrmMappingInput: React.FC<CrmMappingInputProps> = ({
  field,
  activeStep,
  setActiveStep,
  selectedFieldApiName,
  setSelectedFieldApiName,
  availableFields,
  isLoading,
  isFieldComplete,
  isFieldComboboxOpen,
  setIsFieldComboboxOpen,
  typeChangeModal,
  duplicateModal,
  maxLengthModal,
  leadingZeroWarningModal,
  locatorRequiredModalOpen,
  setLocatorRequiredModalOpen,
  handleConfirmStep,
  handlePathChange,
  handleConfirmMapping,
  handleTypeChangeModalClose,
  handleTypeChangeModalConfirm,
  handleDuplicateModalClose,
  handleMaxLengthModalClose,
  handleLeadingZeroWarningClose,
}) => {
  // Add refs for step contents
  const pathRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);

  // Scroll to the active step when it changes
  useEffect(() => {
    let targetRef;
    if (activeStep === 'path') {
      targetRef = pathRef;
    } else if (activeStep === 'field') {
      targetRef = fieldRef;
    }

    if (targetRef?.current) {
      // Small delay to ensure content is rendered
      const timeoutId = setTimeout(() => {
        targetRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);

      // Cleanup function to clear timeout
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [activeStep]);

  return (
    <div className="space-y-1 bg-white border rounded-md">
      {/* Path Step */}
      <div className="border-b">
        <StepHeader
          title="1. Define Path"
          step="path"
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          isComplete={field.isPathConfirmed || false}
          tooltip="Pick the related object that contains this field, starting from your main record. Locators will appear inline for child relationships."
        />
        {activeStep === 'path' && (
          <div ref={pathRef} className="p-4 space-y-4">
            <PathBuilder
              path={field.path || []}
              onPathChange={handlePathChange}
              fieldApiName={field.fieldApiName}
            />
            <div className="flex justify-end">
              <Button onClick={() => handleConfirmStep('path')}>Confirm Path & Proceed</Button>
            </div>
          </div>
        )}
      </div>

      {/* Field Step */}
      <div>
        <StepHeader
          title="2. Select Field"
          step="field"
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          isComplete={isFieldComplete}
          tooltip="Select the specific CRM field to map to."
        />
        {activeStep === 'field' && (
          <div ref={fieldRef} className="p-4 space-y-4">
            <Combobox
              options={
                availableFields?.map((f: CrmField) => ({
                  value: f.id,
                  label: `${f.label} (${f.id})`,
                })) || []
              }
              value={selectedFieldApiName}
              onChange={setSelectedFieldApiName}
              placeholder={isLoading ? 'Loading fields...' : 'Select a field...'}
              searchPlaceholder="Search fields..."
              noResultsMessage="No fields found."
              disabled={!availableFields || isLoading}
              open={isFieldComboboxOpen}
              onOpenChange={setIsFieldComboboxOpen}
            />
            <div className="flex justify-end">
              <Button onClick={handleConfirmMapping} disabled={!selectedFieldApiName}>
                Confirm Mapping
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Type Change Confirmation Modal */}
      <TypeChangeModal
        isOpen={typeChangeModal.isOpen}
        onClose={handleTypeChangeModalClose}
        onConfirm={handleTypeChangeModalConfirm}
        compatibleTypes={typeChangeModal.compatibleTypes}
        currentFormType={field.type}
        crmFieldType={typeChangeModal.selectedCrmField?.type || ''}
        crmFieldLabel={typeChangeModal.selectedCrmField?.label || ''}
      />

      {/* Duplicate Mapping Warning Modal */}
      <DuplicateMappingWarningModal
        isOpen={duplicateModal.isOpen}
        onClose={handleDuplicateModalClose}
        newFieldLabel={field.label}
        conflictingFields={duplicateModal.conflictingFields}
        crmFieldLabel={duplicateModal.crmFieldLabel}
      />

      {/* Max Length Validation Modal */}
      <MaxLengthValidationModal
        isOpen={maxLengthModal.isOpen}
        onClose={handleMaxLengthModalClose}
        fieldLabel={maxLengthModal.validationError?.fieldLabel || ''}
        formFieldMaxLength={maxLengthModal.validationError?.formFieldMaxLength || 0}
        crmFieldLength={maxLengthModal.validationError?.crmFieldLength || 0}
        crmFieldLabel={maxLengthModal.validationError?.crmFieldLabel || ''}
      />

      {/* Leading Zero Warning Modal */}
      <LeadingZeroWarningModal
        isOpen={leadingZeroWarningModal.isOpen}
        onClose={handleLeadingZeroWarningClose}
        onConfirm={leadingZeroWarningModal.onConfirm}
        formFieldLabel={leadingZeroWarningModal.formFieldLabel}
        crmFieldLabel={leadingZeroWarningModal.crmFieldLabel}
        crmFieldType={leadingZeroWarningModal.crmFieldType}
      />

      {/* Locator Required Modal */}
      <LocatorRequiredModal
        isOpen={locatorRequiredModalOpen}
        onClose={() => setLocatorRequiredModalOpen(false)}
      />
    </div>
  );
};
